import { useState, useEffect } from "react";
import axios from "axios";
import "./Dashboard.css";
import Sidebar from "../components/Layout/Sidebar";
import { useDispatch, useSelector } from "react-redux";
import { fetchUser } from "../redux/authSlice";
import { updateProfile, uploadUserProfileImage } from "../redux/profileFilterSlice";
import { deleteProfile } from "../redux/profileFilterSlice";
import { fetchMatches } from "../redux/matchFilterSlice";
import { Link, useNavigate } from "react-router-dom";
import userAvatar from "../img/user-avatar.png";
import { fetchUserInterests } from "../redux/interestSlice";
import { useRef } from "react";
import Loader from "./Loader";
import { useTranslation } from "react-i18next";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

const defaultForm = {
  fullName: "",
  age: "",
  religion: "",
  caste: "",
  location: {
    state: "",
    city: "",
  },
  profession: "",
  professionDetails: "",
  salary: "",
  image: "",
  education: "",
  height: "",
  motherTongue: "",
  maritalStatus: "",
  gender: "",
  diet: "",
  smoking: "No",
  drinking: "No",
  about: "",
  prefAgeMin: "",
  prefAgeMax: "",
  prefHeight: "",
  prefReligion: "",
  prefCaste: "",
  prefState: "",
  prefCity: "",
  prefEducation: "",
  prefProfession: "",
  prefProfessionDetails: "",
  prefSalaryMin: "",
};

const heights = [
  "4'10\"", "5'0\"", "5'2\"", "5'4\"", "5'6\"", "5'8\"", "5'10\"",
  "6'0\"", "6'2\"", "6'4\"", "6'6\"", "6'8\"", "6'10\"", "7'0\"",
];

// const incomeRanges = ["3–6L", "6–12L", "12–25L", "25L+"];
const incomeRanges = [
  "Below 10K",
  "10K – 25K",
  "25K – 50K",
  "50K – 1L",
  "1L – 2L",
  "2L+"
];

const TABS = ["Overview", "Personal", "Professional", "Lifestyle", "Preferences", "Gallery"];

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

// ── Helper: always return a scalar string from a value that might be array ──
const toScalar = (val) => {
  if (Array.isArray(val)) return val[0] || "";
  return val || "";
};

function Avatar({ profileId, color, name, profileIcon, photos }) {
  const imageUrl = photos?.url
    ? `${process.env.REACT_APP_UPLOAD_IMAGE_URL}/${photos.url}`
    : userAvatar || "/default-avatar.png";

  return (
    <div className="avatar" style={{ background: color }}>
      <Link to={`/profile/${profileId}`} className="avatar-link">
        <img src={imageUrl} alt="profile" className="avatar-img" />
      </Link>
    </div>
  );
}

export default function Dashboard({ user, onLogout }) {

  const [cropSrc, setCropSrc] = useState(null);         // raw image preview URL
const [crop, setCrop] = useState();                    // crop box position
const [completedCrop, setCompletedCrop] = useState();  // final crop values
const [showCropModal, setShowCropModal] = useState(false);
const [pendingFile, setPendingFile] = useState(null);  // original file saved
const [pendingUserId, setPendingUserId] = useState(null);
const imgRef = useRef(null);                           // ref to the <img> inside crop

  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
const ref = useRef(null);
  // ── Redux state ──────────────────────────────────────────────────────────────
  const userFromStore = useSelector((state) => state.auth.user);
  const profileFromStore = useSelector((state) => state.auth.profile);
  const { list } = useSelector((state) => state.matches);
  const userFromLoginResponse = useSelector((state) => state.auth.loginResponse);
  const { recommendedMatches } = useSelector((state) => state.profile);

  // ── Local state ──────────────────────────────────────────────────────────────
  const [profile, setProfile] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [caste, setCaste] = useState([]);
  const [activeTab, setActiveTab] = useState("Overview");
  const [shortlisted, setShortlisted] = useState([]);
  const [activeFormTab, setActiveFormTab] = useState("basic");
  const [profileImage, setProfileImage] = useState(null);
  const [allFilterTypes, setAllFilterTypes] = useState([]);
  const [education, setEducation] = useState([]);
  const [profession, setProfession] = useState([]);
  const [motherTongue, setMotherTongue] = useState([]);
  const [userType, setUserType] = useState("");
  const [recommendedProfiles, setRecommendedProfiles] = useState([]);
  const interests = useSelector((state) => state.interests.data);
  const [counts, setCounts] = useState();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // ── Derived display values ───────────────────────────────────────────────────
  const displayName =
    user?.username || user?.name ||
    userFromStore?.username || userFromStore?.name || userFromStore?.profile?.fullName || "User";

  const displayEmail = user?.email || "";

  const memberSince =
    user?.createdAt || userFromStore?.createdAt
      ? new Date(user?.createdAt || userFromStore?.createdAt).toLocaleDateString("en-IN", {
          month: "short",
          year: "numeric",
        })
      : "Member";

  useEffect(() => {
  const handler = (e) => {
    if (ref.current && !ref.current.contains(e.target)) setOpen(false);
  };
  document.addEventListener("mousedown", handler);
  return () => document.removeEventListener("mousedown", handler);
}, []);

  useEffect(() => {
      dispatch(fetchUserInterests());
    }, [dispatch]);

    useEffect(() => {
    const counts = interests?.counts;   
    setCounts(counts);
  }, [interests]);

  //  Single useEffect for list?.data (removed duplicate)
  useEffect(() => {
    if (list?.data) {
      const types = list.data;
      setAllFilterTypes(types);

      const castes = types.filter(
        (f) => f.masterType === "Caste" && f.parentId?._id !== null
      );
      setCaste(castes);

      const professions = types.filter((f) => f.masterType === "Occupation");
      setProfession(professions);

      const education = types.filter((f) => f.masterType === "Education");
      setEducation(education);

      //get mother tongue list
    const motherTongue = types?.filter((f) => f.isActive && f.masterType === "Mother tongue")
      .map((m) => m);
    setMotherTongue(motherTongue);
    console.log("motherTongue:", motherTongue);
    }
  }, [list]);

  useEffect(() => {
    if (recommendedMatches?.matches) {
      const profiles = recommendedMatches.matches.slice(0, 4).map((match) => {
        const profileURL = match.photos.find((photo) => photo.isPrimary);
        return {
          _id: match._id,
          name: match?.fullName ?? `${match.firstName} ${match.lastName}`,
          age: match.age,
          location: match.location,
          photos: profileURL ?? userAvatar,
          matchPercent: match.matchPercent,
        };
      });
      setRecommendedProfiles(profiles);
    }
  }, [recommendedMatches]);

  useEffect(() => {
    if (userFromLoginResponse) setUserType(userFromLoginResponse);
  }, [userFromLoginResponse]);

  useEffect(() => {
    dispatch(fetchMatches());
  }, [dispatch]);

  useEffect(() => {
    setLoading(true);
    dispatch(fetchUser()).unwrap().then((res) => {
      setLoading(false);
    }).catch(() => setLoading(false));
    }, [dispatch]);

  useEffect(() => {
  if (userFromStore?.profile) {
    setProfile(userFromStore.profile);
    localStorage.setItem("profileId", userFromStore.profile._id);
    checkRequiredFields(userFromStore?.profile);
  }
}, [userFromStore]);

  useEffect(() => {
    if (profile?.photos?.length) {
      const primary = profile.photos.find((p) => p.isPrimary);
      setProfileImage(primary?.url || profile.photos[0]?.url);
    }
  }, [profile]);

  useEffect(() => {
    if (!editId) {
      setForm((prev) => ({ ...prev, name: displayName }));
    }
  }, [displayName, editId]);

  // ── Toast helper ─────────────────────────────────────────────────────────────
  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ── Form handlers ────────────────────────────────────────────────────────────
  const handleChange = (e) =>{
    console.log(e.target.name, e.target.value);
    
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  const handleSubmit = async () => {    
    console.log('handleSubmit:', form);
    
    // if (!form.fullName && !form.name) return showToast("Name is required", "error");
    // if (!form.age) return showToast("Age is required", "error");
      // add validation form filds here if needed
  if (!form?.fullName || !form?.age || !form?.diet || (form?.gender !== "Female" &&
    (!form?.smoking || !form?.drinking)) || !form?.profession || !form?.education) {
    showToast("Fill all required fields", "error");
    return;
  }
    //make about content if empty
    if (!form.about) {
      form.about = `Hi, I'm ${form.fullName || form.name}. Looking for a compatible match.`;
    }

    try {
      if (editId) {
        console.log('Updating profile:', { ...form, id: editId });
        await dispatch(updateProfile({ ...form, id: editId })).unwrap();
        await dispatch(fetchUser()).unwrap();
        showToast("Profile updated!");
      } else {
        console.log('Saving profile:', form);
        await dispatch(updateProfile({ ...form })).unwrap();
        await dispatch(fetchUser()).unwrap();
        showToast("Profile saved!");
      }
      setEditId(null);
      setShowForm(false);
    } catch (err) {
      showToast(err?.response?.data?.message || "Error saving profile", "error");
    }
    setLoading(false);
  };

  const handleEdit = (p) => {
    setForm({
      fullName: p.fullName || "",
      age: p.age || "",
      religion: p.religion || "",
      caste: p.caste || "",
      location: {
        state: p.location?.state || "",
        city: p.location?.city || "",
      },
      profession: p.profession || "",
      professionDetails: p.professionDetails || "",
      salary: p.salary || "",
      image: p.image || "",
      education: p.education || "",
      height: p.height || "",
      motherTongue: p.motherTongue || "",
      maritalStatus: p.maritalStatus || "",
      gender: p.gender || "",
      diet: p.diet || "",
      smoking: p.smoking || "",
      drinking: p.drinking || "",
      about: p.about || p?.aboutMe || "",
      prefAgeMin: p.partnerPreferences?.ageFrom || p.prefAgeMin || "",
      prefAgeMax: p.partnerPreferences?.ageTo || p.prefAgeMax || "",
      prefHeight: toScalar(p.partnerPreferences?.height || p.prefHeight),

      prefReligion: toScalar(
        Array.isArray(p.partnerPreferences?.religion)
          ? p.partnerPreferences.religion[0]
          : p.partnerPreferences?.religion || p.prefReligion
      ),
      prefCaste: toScalar(
        Array.isArray(p.partnerPreferences?.caste)
          ? p.partnerPreferences.caste[0]
          : p.partnerPreferences?.caste || p.prefCaste
      ),
      prefState: toScalar(p.partnerPreferences?.state || p.prefState),
      prefCity: toScalar(p.partnerPreferences?.city || p.prefCity),   
      prefEducation: toScalar(
        Array.isArray(p.partnerPreferences?.education)
          ? p.partnerPreferences.education[0]
          : p.partnerPreferences?.education || p.prefEducation
      ),
      prefProfession: toScalar(p.partnerPreferences?.profession || p.prefProfession),
      prefProfessionDetails:p.partnerPreferences?.professionDetails || p.prefProfessionDetails,
      prefSalaryMin: toScalar(p.partnerPreferences?.salary || p.prefSalaryMin),
    });
    setEditId(p._id);
    setShowForm(true);
    setActiveFormTab("basic");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this profile?")) return;
    try {
      await dispatch(deleteProfile(id)).unwrap();
      showToast("Profile deleted");
      setProfile(null);
    } catch {
      showToast("Error deleting profile", "error");
    }
  };

  const handleImageUpload = async (e, userId) => {
  const file = e.target.files[0];
  if (!file) return;

  // ── File type validation ──
  const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
  if (!allowedTypes.includes(file.type)) {
    showToast("Only JPG, PNG, WEBP, or GIF images are allowed", "error");
    e.target.value = "";
    return;
  }

  // ── File size validation (2MB max) ──
  if (file.size > 2 * 1024 * 1024) {
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
    showToast(
      `Image too large (${fileSizeMB} MB). Maximum allowed size is 2MB`,
      "error"
    );
    e.target.value = "";
    return;
  }

  // ── Photo limit check ──
  if (profile?.photos?.length >= 4) {
    showToast("Maximum 4 photos allowed. Remove one to upload more.", "error");
    e.target.value = "";
    return;
  }

  const reader = new FileReader();
  reader.onload = () => {
    setCropSrc(reader.result);       // base64 preview
    setPendingFile(file);            // save file for later upload
    setPendingUserId(userId);
    setShowCropModal(true);
  };
  reader.readAsDataURL(file);
  e.target.value = "";               // reset input
};

const handleCropConfirm = async () => {
  if (!completedCrop || !imgRef.current) {
    showToast("Please select a crop area", "error");
    return;
  }

  // ── Draw cropped area to canvas ──
  const canvas = document.createElement("canvas");
  const img = imgRef.current;
  const scaleX = img.naturalWidth / img.width;
  const scaleY = img.naturalHeight / img.height;

  canvas.width = 500;
canvas.height = 494;

  const ctx = canvas.getContext("2d");
  ctx.drawImage(
  img,
  completedCrop.x * scaleX,
  completedCrop.y * scaleY,
  completedCrop.width * scaleX,
  completedCrop.height * scaleY,
  0,
  0,
  500,  // always output 500px wide
  494   // always output 494px tall
);

  canvas.toBlob(async (blob) => {
    if (!blob) {
      showToast("Crop failed. Try again.", "error");
      return;
    }

    const croppedFile = new File([blob], pendingFile.name, { type: pendingFile.type });
    const formData = new FormData();
    formData.append("image", croppedFile);

    setShowCropModal(false);
    setCropSrc(null);

    try {
      const res = await dispatch(
        uploadUserProfileImage({ profileId: pendingUserId, formData })
      ).unwrap();

      if (res.profile) {
        setProfile(res.profile);
        await dispatch(fetchUser()).unwrap();
        const photos = res.profile?.photos;
        if (photos?.length) {
          const primary = photos.find((p) => p.isPrimary);
          setProfileImage(primary?.url || photos[0]?.url);
        }
        showToast("Photo uploaded successfully!");
      }
    } catch (err) {
      showToast("Image upload failed", "error");
      console.error(err);
    }
  }, pendingFile.type, 0.92);
};

  const handleSetPrimary = async (photoUrl) => {
    try {
      await dispatch(updateProfile({ id: profile._id, primaryPhoto: photoUrl })).unwrap();
      await dispatch(fetchUser()).unwrap();
      setProfileImage(photoUrl);
      showToast("Profile photo updated!");
    } catch {
      showToast("Failed to set profile photo", "error");
    }
  };

  const handleDeletePhoto = async (photoUrl) => {
    if (!window.confirm("Remove this photo?")) return;
    try {
      await dispatch(updateProfile({ id: profile._id, removePhoto: photoUrl })).unwrap();
      await dispatch(fetchUser()).unwrap();
      const remaining = profile.photos.filter((p) => p.url !== photoUrl);
      if (profileImage === photoUrl) {
        const next = remaining.find((p) => p.isPrimary) || remaining[0];
        setProfileImage(next?.url || null);
      }
      showToast("Photo removed!");
    } catch {
      showToast("Failed to remove photo", "error");
    }
  };

  const getCastBasedonReligion = (religionId) => {
    if (!religionId) {
      setCaste(allFilterTypes.filter((f) => f.masterType === "Caste"));
      return;
    }
    const castes = allFilterTypes.filter(
      (f) => f.masterType === "Caste" && f.parentId?._id === religionId
    );
    setCaste(castes);
  };

  const [compatibilityScore] = useState(() => Math.floor(60 + Math.random() * 35));

  const stats = {
    total: profile ? 1 : 0,
    matched: profile ? Math.floor(Math.random() * 40 + 10) : 0,
    shortlisted: shortlisted.length,
    viewed: profile ? Math.floor(Math.random() * 20 + 5) : 0,
  };

  const checkRequiredFields = (p) => {
    console.log('Checking required fields for:', p);
    
    setForm({
      fullName: p?.fullName || "",
      age: p?.age || "",
      religion: p?.religion || "",
      caste: p?.caste || "",
      location: {
        state: p?.location?.state || "",
        city: p?.location?.city || "",
      },
      profession: p?.profession || "",
      professionDetails: p?.professionDetails || "",
      salary: p?.salary || "",
      image: p?.image || "",
      education: p?.education || "",
      height: p?.height || "",
      motherTongue: p?.motherTongue || "",
      maritalStatus: p?.maritalStatus || "",
      gender: p?.gender || "",
      diet: p?.diet || "",
      smoking: p?.smoking || "No",
      drinking: p?.drinking || "No",
      about: p?.about || "",
      prefAgeMin: p?.partnerPreferences?.ageFrom || p?.prefAgeMin || "",
      prefAgeMax: p?.partnerPreferences?.ageTo || p?.prefAgeMax || "",
      prefHeight: toScalar(p?.partnerPreferences?.height || p?.prefHeight),

      prefReligion: toScalar(
        Array.isArray(p?.partnerPreferences?.religion)
          ? p?.partnerPreferences?.religion[0]
          : p?.partnerPreferences?.religion || p?.prefReligion
      ),
      prefCaste: toScalar(
        Array.isArray(p?.partnerPreferences?.caste)
          ? p?.partnerPreferences?.caste[0]
          : p?.partnerPreferences?.caste || p?.prefCaste
      ),
      prefState: toScalar(p?.partnerPreferences?.state || p?.prefState),
      prefCity: toScalar(p?.partnerPreferences?.city || p?.prefCity),   
      prefEducation: toScalar(
        Array.isArray(p?.partnerPreferences?.education)
          ? p?.partnerPreferences?.education[0]
          : p?.partnerPreferences?.education || p?.prefEducation
      ),
      prefProfession: toScalar(p?.partnerPreferences?.profession || p?.prefProfession),
      prefSalaryMin: toScalar(p?.partnerPreferences?.salary || p?.prefSalaryMin),
    });
    
    if (!p?.fullName || !p?.age || !p?.location?.state || !p?.location?.city || !p?.profession || !p?.education) {
      //open form model
      setEditId(p._id);
    setShowForm(true);
    setActiveFormTab("basic");
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  // get localstorage language and set to i18n
    const language = localStorage.getItem("lang") || "en";
  return (
    <>
      <div className="app">
        { loading ? <Loader /> : '' }
        <Sidebar profileImage={profileImage} profileDetails={profile} />

        <main className="main">
          {/* Header */}
          <div className="main-header">
            <div className="main-header-left">
              <h1 className="page-title">
                 <span>Dashboard</span>
              </h1>
              <p className="page-sub">
                Welcome back ,<span>{displayName} </span>
              </p>
            </div>
            <div className="main-header-right"></div>
          </div>

          {/* Stats */}
          <div className="stats-row">
            <div className="stat-card" onClick={()=>navigate('/interest')}>
              <div className="stat-icon"></div>
              <div className="stat-value">{counts?.received || 0}</div>
              {/* <div className="stat-label">My Profile</div> */}
              <div className="stat-label">Intrest Received</div>
            </div>
            <div className="stat-card" onClick={()=>navigate('/interest')}>
              <div className="stat-icon"></div>
              <div className="stat-value">{counts?.sent || 0}</div>
              {/* <div className="stat-label">Matches Found</div> */}
              <div className="stat-label">Intrest Sent</div>
            </div>
            <div className="stat-card" onClick={()=>navigate('/interest')}>
              <div className="stat-icon"></div>
              <div className="stat-value">{counts?.accepted || 0}</div>
              {/* <div className="stat-label">Shortlisted</div> */}
              <div className="stat-label">Accepted</div>
            </div>
            <div className="stat-card" onClick={()=>navigate('/interest')}>
              <div className="stat-icon"></div>
              <div className="stat-value">{counts?.rejected || 0}</div>
              {/* <div className="stat-label">Profiles Viewed</div> */}
              <div className="stat-label">Declined</div>
            </div>
          </div>

          {/* Account strip */}
          <div className="account-strip">
            <div className="as-item">
              <span className="as-icon"></span>
              <div>
                <div className="as-label">Logged in as</div>
                <div className="as-value">{userType?.role === "admin" ? "Admin" : "User"}</div>
              </div>
            </div>
            <div className="as-divider" />
            <div className="as-item">
              <span className="as-icon"></span>
              <div>
                <div className="as-label">Email</div>
                <div className="as-value">{profile?.userId?.email}</div>
              </div>
            </div>
            <div className="as-divider" />
            <div className="as-item">
              <span className="as-icon"></span>
              <div>
                <div className="as-label">Member Since</div>
                <div className="as-value">{memberSince}</div>
              </div>
            </div>
            <div className="as-divider" />
            <div className="as-item">
              <span className="as-icon"></span>
              <div>
                <div className="as-label">Account</div>
                <div className="as-value as-verified">Verified</div>
              </div>
            </div>
          </div>

          {/* Profile detail or empty state */}
          {profile ? (
            <div className="profile-detail" key={profile._id}>
              {/* Hero */}
              <div className="profile-hero">
                <div className="hero-avatar">
                  {profileImage ? (
                    <img
                      src={`${process.env.REACT_APP_UPLOAD_IMAGE_URL}/${profileImage}`}
                      alt="profile"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "top center",
                      }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = userAvatar;
                      }}
                    />
                  ) : (
                    <Avatar
                      profileId={profile._id}
                      color={profile?.color}
                      name={profile?.name}
                      profileIcon={profile?.profileIcon}
                      photos={profile?.photos}
                    />
                  )}
                </div>
                <div className="hero-info">
                  <h2>
                    {profile?.userId?.userName || displayName}
                    {profile?.age ? `, ${profile.age}` : ""}
                  </h2>
                  <p className="hero-location">
                    {`${profile?.location?.city || ""}, ${profile?.location?.state || ""}` ||
                      "Location not set"}
                  </p>
                  {profile?.about && <p className="hero-about">{profile.about}</p>}
                  <div className="hero-tags">
                    {profile?.religion && <span className="tag tag-religion">{profile.religion}</span>}
                    {profile?.caste && <span className="tag tag-caste">{profile.caste}</span>}
                    {profile?.profession && <span className="tag tag-prof">{profile.profession}</span>}
                    {profile?.salary && <span className="tag tag-salary">{profile.salary}</span>}
                    {profile?.education && <span className="tag tag-edu">{profile.education}</span>}
                    {profile?.height && <span className="tag tag-height">{profile.height}</span>}
                    {profile?.gender && <span className="tag tag-gender">{profile.gender}</span>}
                    {profile?.maritalStatus && <span className="tag tag-marital">{profile.maritalStatus}</span>}
                  </div>
                </div>
                <div className="hero-side">
                  {/* <div className="compat-ring">
                    <svg viewBox="0 0 80 80" width="80" height="80">
                      <circle cx="40" cy="40" r="32" fill="none" stroke="#f0e6ff" strokeWidth="8" />
                      <circle
                        cx="40" cy="40" r="32"
                        fill="none"
                        stroke="url(#grad)"
                        strokeWidth="8"
                        strokeDasharray={`${compatibilityScore * 2.01} 201`}
                        strokeLinecap="round"
                        transform="rotate(-90 40 40)"
                      />
                      <defs>
                        <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#c084fc" />
                          <stop offset="100%" stopColor="#f472b6" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <div className="compat-text">
                      <div className="compat-num">{compatibilityScore}%</div>
                      <div className="compat-label">Match</div>
                    </div>
                  </div> */}
                  <div className="hero-actions">
                    <button className="btn-edit" onClick={() => handleEdit(profile)}>Edit</button>
                    <button className="btn-delete" onClick={() => handleDelete(profile._id)}>Delete</button>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="tab-nav">
                {TABS.map((tab) => (
                  <button
                    key={tab}
                    className={`tab-btn ${activeTab === tab ? "active" : ""}`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              <div className="tab-content">
                {activeTab === "Overview" && (
                  <div className="profile-body">
                    <div className="detail-section">
                      <div className="section-title">Personal Details</div>
                      {[
                        ["Full Name", profile?.userId?.userName || displayName],
                        ["Age", profile.age ? `${profile.age} yrs` : null],
                        ["Height", profile.height],
                        ["Gender", profile.gender],
                        ["Religion", profile.religion],
                        ["Caste", profile.caste],
                        ["Location", `${profile?.location?.city || ""}, ${profile?.location?.state || ""}`],
                        ["Mother Tongue", profile.motherTongue],
                        ["Marital Status", profile.maritalStatus],
                      ].map(([label, value]) => (
                        <div className="detail-row" key={label}>
                          <span className="detail-label">{label}</span>
                          <span className="detail-value">{value || "—"}</span>
                        </div>
                      ))}
                    </div>
                    <div className="detail-section">
                      <div className="section-title">Professional Details</div>
                      {[
                        ["Profession", profile.profession === "Others" ? profile.professionDetails : profile.profession],
                        ["Education", profile.education],
                        ["Salary", profile.salary],
                      ].map(([label, value]) => (
                        <div className="detail-row" key={label}>
                          <span className="detail-label">{label}</span>
                          <span className="detail-value">{value || "—"}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === "Personal" && (
                  <div className="profile-body single">
                    <div className="detail-section full">
                      <div className="section-title">Personal Information</div>
                      {[
                        ["Full Name", profile?.userId?.userName || displayName],
                        ["Age", profile.age ? `${profile.age} years` : null],
                        ["Height", profile.height],
                        ["Gender", profile.gender],
                        ["Marital Status", profile.maritalStatus],
                        ["Mother Tongue", profile.motherTongue],
                        ["Religion", profile.religion],
                        ["Caste / Community", profile.caste],
                        ["City / Location", profile?.location?.city || profile?.location?.state || null],
                      ].map(([label, value]) => (
                        <div className="detail-row" key={label}>
                          <span className="detail-label">{label}</span>
                          <span className="detail-value">{value || "—"}</span>
                        </div>
                      ))}
                    </div>
                    {profile.about && (
                      <div className="detail-section full">
                        <div className="section-title">About Me</div>
                        <p className="about-text">{profile.about}</p>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === "Professional" && (
                  <div className="profile-body single">
                    <div className="detail-section full">
                      <div className="section-title">Career & Education</div>
                      {[
                        ["Profession", profile.profession === "Others" ? profile.professionDetails : profile.profession],
                        ["Education", profile.education],
                        ["Annual Salary", profile.salary],
                      ].map(([label, value]) => (
                        <div className="detail-row" key={label}>
                          <span className="detail-label">{label}</span>
                          <span className="detail-value">{value || "—"}</span>
                        </div>
                      ))}
                    </div>
                    <div className="career-banner">
                      <div className="career-icon"></div>
                      <div>
                        <div className="career-title">{profile.profession || "Professional"}</div>
                        <div className="career-sub">
                          {profile.education || "Education not listed"} ·{" "}
                          {profile.salary || "Salary not disclosed"}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "Lifestyle" && (
                  <div className="profile-body single">
                    <div className="detail-section full">
                      <div className="section-title">Lifestyle & Habits</div>
                      {[
                        ["Diet", profile.diet],
                        ["Smoking", profile.smoking],
                        ["Drinking", profile.drinking],
                      ].map(([label, value]) => (
                        <div className="detail-row" key={label}>
                          <span className="detail-label">{label}</span>
                          <span className="detail-value">{value || "Not specified"}</span>
                        </div>
                      ))}
                    </div>
                    <div className="lifestyle-badges">
                      {[profile.diet, profile.smoking, profile.drinking]
                        .filter(Boolean)
                        .map((val) => (
                          <div className="lbadge" key={val}>{val}</div>
                        ))}
                    </div>
                  </div>
                )}

                {activeTab === "Preferences" && (
                  <div className="profile-body single">
                    <div className="detail-section full">
                      <div className="section-title">Partner Preferences</div>
                      {[
                        [
                          "Age Range",
                          profile?.partnerPreferences?.ageFrom && profile?.partnerPreferences?.ageTo
                            ? `${profile.partnerPreferences.ageFrom} – ${profile.partnerPreferences.ageTo} years`
                            : profile?.prefAgeMin && profile?.prefAgeMax
                            ? `${profile.prefAgeMin} – ${profile.prefAgeMax} years`
                            : null,
                        ],
                        ["Height", profile?.partnerPreferences?.height || null],
                        [
                          "Religion",
                          profile?.partnerPreferences?.religion?.length
                            ? profile.partnerPreferences.religion.join(", ")
                            : profile?.prefReligion || null,
                        ],
                        [
                          "Caste",
                          profile?.partnerPreferences?.caste?.length
                            ? profile.partnerPreferences.caste.join(", ")
                            : profile?.prefCaste || null,
                        ],
                        ["State", profile?.partnerPreferences?.state || null],
                        ["City", profile?.partnerPreferences?.city || null],
                        [
                          "Education",
                          profile?.partnerPreferences?.education?.length
                            ? profile.partnerPreferences.education.join(", ")
                            : profile?.prefEducation || null,
                        ],
                        ["Profession", profile?.partnerPreferences?.profession['0'] === "Others" ? profile.partnerPreferences.professionDetails : profile?.partnerPreferences?.profession || null],
                        ["Min. Salary", profile?.partnerPreferences?.salary || null],
                      ].map(([label, value]) => (
                        <div className="detail-row" key={label}>
                          <span className="detail-label">{label}</span>
                          <span className="detail-value">{value || "Any"}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === "Gallery" && (
                  <div className="gallery-section">
                    <div className="section-title">
                      Photo Gallery ({profile?.photos?.length || 0})
                    </div>
                    <div
                      className="gallery-grid"
                      style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 20 }}
                    >
                      {profile?.photos?.map((photo, index) => {
                        const isPrimary = photo.isPrimary || photo.url === profileImage;
                        return (
                          <div
                            key={index}
                            className="gallery-item"
                            style={{
                              position: "relative",
                              borderRadius: 12,
                              overflow: "hidden",
                              border: isPrimary ? "3px solid #c084fc" : "3px solid transparent",
                              boxShadow: isPrimary
                                ? "0 0 0 2px #f472b6"
                                : "0 2px 8px rgba(0,0,0,0.1)",
                            }}
                          >
                         <img
                            src={`${process.env.REACT_APP_UPLOAD_IMAGE_URL}/${photo.url}`}
                            alt={`photo-${index}`}
                            style={{
                              width: "100%",
                              height: 300,
                              objectFit: "cover",
                              objectPosition: "top center",  // ← face shown, not feet
                              display: "block",
                            }}
                            onError={(e) => (e.target.style.display = "none")}
                          />
                            {isPrimary && (
                              <div
                                style={{
                                  position: "absolute", top: 8, left: 8,
                                  background: "linear-gradient(135deg, #c084fc, #f472b6)",
                                  color: "#fff", fontSize: 11, fontWeight: 700,
                                  padding: "3px 8px", borderRadius: 20,
                                }}
                              >
                                Profile Photo
                              </div>
                            )}
                            <div
                              style={{
                                position: "absolute", bottom: 0, left: 0, right: 0,
                                display: "flex", gap: 6, padding: 8,
                                background: "linear-gradient(transparent, rgba(0,0,0,0.6))",
                              }}
                            >
                              {!isPrimary && (
                                <button
                                  onClick={() => handleSetPrimary(photo.url)}
                                  style={{
                                    flex: 1, padding: "5px 0", fontSize: 12, fontWeight: 600,
                                    background: "rgba(192,132,252,0.9)", color: "#fff",
                                    border: "none", borderRadius: 8, cursor: "pointer",
                                  }}
                                >
                                  Set as Profile
                                </button>
                              )}
                              <button
                                onClick={() => handleDeletePhoto(photo.url)}
                                style={{
                                  padding: "5px 10px", fontSize: 12, fontWeight: 600,
                                  background: "rgba(239,68,68,0.85)", color: "#fff",
                                  border: "none", borderRadius: 8, cursor: "pointer",
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        );
                      })}

                      {(!profile?.photos || profile.photos.length < 4) && (
                        <label
                          style={{
                            display: "flex", flexDirection: "column", alignItems: "center",
                            justifyContent: "center", height: 180, borderRadius: 12, cursor: "pointer",
                            border: "2px dashed #c084fc", background: "#faf5ff", color: "#9333ea",
                            fontSize: 13, fontWeight: 600, gap: 8,
                          }}
                        >
                          <span style={{ fontSize: 32 }}>+</span>
                          Add Photo
                          <span style={{ fontSize: 11, color: "#a78bfa", fontWeight: 400 }}>
                            JPG, PNG, WEBP up to 2MB
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            style={{ display: "none" }}
                            onChange={(e) =>
                              handleImageUpload(e, profile.userId?._id || profile.userId)
                            }
                          />
                        </label>
                      )}
                    </div>
                    <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center" }}>
                      Click <strong>"Set as Profile"</strong> on any photo to use it as your main profile picture.
                    </p>
                  </div>
                )}
              </div>

              {/* Timeline */}
              <div className="timeline-section">
                <div className="section-title">Recent Activity</div>
                <div className="timeline">
                  <div className="timeline-item">
                    <div className="timeline-dot" />
                    <div className="timeline-content">
                      <div className="timeline-title">Profile Created</div>
                      <div className="timeline-sub">Account registered and verified</div>
                    </div>
                    <div className="timeline-time">{memberSince}</div>
                  </div>
                  <div className="timeline-item">
                    <div className="timeline-dot accent" />
                    <div className="timeline-content">
                      <div className="timeline-title">Profile Viewed</div>
                      <div className="timeline-sub">This profile was recently viewed</div>
                    </div>
                    <div className="timeline-time">Today</div>
                  </div>
                  {shortlisted.includes(profile._id) && (
                    <div className="timeline-item">
                      <div className="timeline-dot pink" />
                      <div className="timeline-content">
                        <div className="timeline-title">Shortlisted</div>
                        <div className="timeline-sub">Added to your shortlist</div>
                      </div>
                      <div className="timeline-time">Just now</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon"></div>
              <h3>Profile Not Set Up Yet</h3>
              <p>Complete your profile so others can find you.</p>
              <button
                className="btn-edit"
                onClick={() => {
                  setEditId(null);
                  setForm({ ...defaultForm, name: displayName });
                  setShowForm(true);
                  setActiveFormTab("basic");
                }}
              >
                + Add Profile Information
              </button>
            </div>
          )}
        </main>
      </div>

      {/* ── Profile form modal ── */}
      {showForm && (
        <div
          className="overlay"
          onClick={(e) => e.target === e.currentTarget && setShowForm(false)}
        >
          <div className="modal modal-lg">
            <div className="modal-header">
              <div className="modal-title">
                {editId ? "Edit Profile" : "Add Profile Information"}
                <span className="note-text">
                      Note: Please complete all details to show matched profiles
                    </span>
                </div>
              <button className="close-btn" onClick={() => setShowForm(false)}>✕</button>
            </div>

            {!editId && (
              <div
                style={{
                  background: "linear-gradient(135deg, #f5f0ff, #fce7f3)",
                  borderBottom: "1px solid #e9d5ff",
                  padding: "10px 24px",
                  fontSize: "14px",
                  color: "#6b21a8",
                }}
              >
                Filling information for: <strong>{displayName}</strong>
              </div>
            )}

            <div className="form-tabs">
              {["basic", "professional", "lifestyle", "preferences"].map((t) => (
                <button
                  key={t}
                  className={`form-tab-btn ${activeFormTab === t ? "active" : ""}`}
                  onClick={() => setActiveFormTab(t)}
                >
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>

            <div className="modal-body">
              {/* ── BASIC TAB ── */}
              {activeFormTab === "basic" && (
                <div className="form-grid">
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input
                      name="fullName"
                      value={form.fullName}
                      onChange={handleChange}
                      placeholder="e.g. Arun Kumar"
                      style={
                        !editId
                          ? { background: "#f3f0ff", color: "#6b21a8", fontWeight: 600, cursor: "not-allowed" }
                          : {}
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Age *</label>
                    <input name="age" type="number" value={form.age} onChange={handleChange} placeholder="e.g. 27" />
                  </div>
                  <div className="form-group">
                    <label>Height</label>
  <div className={`hd-wrapper ${open ? "hd-open" : ""}`} ref={ref}>
    
    <button type="button" className="hd-trigger" onClick={() => setOpen((p) => !p)}>
      <span className={form.height ? "hd-value" : "hd-placeholder"}>
        {form.height || "Select"}
      </span>
      <svg className={`hd-chevron ${open ? "hd-chevron--up" : ""}`}
        viewBox="0 0 12 8" fill="none"
        stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <path d="M1 1l5 5 5-5" />
      </svg>
    </button>

    {open && (
      <ul className="hd-list">
        {heights.map((h, i) => (
          <li key={i}
            className={`hd-item ${form.height === h ? "hd-item--active" : ""}`}
            onClick={() => { handleChange({ target: { name: "height", value: h } }); setOpen(false); }}>
            {h}
            {form.height === h && (
              <svg viewBox="0 0 12 10" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" className="hd-tick">
                <path d="M1 5l3.5 3.5L11 1" />
              </svg>
            )}
          </li>
        ))}
      </ul>
    )}

  </div>
                  </div>
                  <div className="form-group">
                    <label>Mother Tongue</label>
                    <select name="motherTongue" value={form.motherTongue} onChange={handleChange}>
                      <option value="">Select Mother Tongue</option>
                      {motherTongue.map((m) => (
                        <option key={m._id} value={m.masterValue?.["en"]}>
                          {m.masterValue?.["en"] || m.masterValue?.en}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Religion</label>
                    <select
                      name="religion"
                      value={form.religion}
                      onChange={(e) => {
                        handleChange(e);
                        const selected = allFilterTypes.find(
                          (f) => f.isActive && f.masterType === "Religion" && f.masterValue?.["en"] === e.target.value
                        );
                        getCastBasedonReligion(selected?._id || null);
                      }}
                    >
                      <option value="">Select Religion</option>
                      {allFilterTypes
                        .filter((f) => f.isActive && f.masterType === "Religion")
                        .map((r) => (
                          <option key={r._id} value={r.masterValue?.["en"]}>
                            {/* {r.masterValue?.[i18n.language] || r.masterValue?.en} */}
                            {language === "en" || language === "ta" ? r.masterValue?.["en"] : r.masterValue?.en }

                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Caste</label>
                    <select name="caste" value={form.caste} onChange={handleChange}>
                      <option value="">Select Caste</option>
                      {caste.map((c) => (
                        <option key={c._id} value={c.masterValue?.["en"]}>
                          {/* {c.masterValue?.[i18n.language] || c.masterValue?.en} */}
                          {language === "en" || language === "ta" ? c.masterValue?.["en"] : c.masterValue?.en }
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>State</label>
                    <select
                      value={form.location.state}
                      onChange={(e) =>
                        setForm({ ...form, location: { ...form.location, state: e.target.value, city: "" } })
                      }
                    >
                      <option value="">Select State</option>
                      {allFilterTypes
                        .filter((f) => f.masterType === "State")
                        .map((s) => (
                          <option key={s._id} value={language === "en" || language === "ta" ? s.masterValue?.["en"] : s.masterValue?.en}>
                            {language === "en" || language === "ta" ? s.masterValue?.["en"] : s.masterValue?.en }
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>My City</label>
                    <select
                      value={form.location.city}
                      onChange={(e) =>
                        setForm({ ...form, location: { ...form.location, city: e.target.value } })
                      }
                    >
                      <option value="">Select City</option>
                      {allFilterTypes
                        .filter(
                          (f) =>
                            f.masterType === "City" &&
                            (form.location.state ? f.parentId?.masterValue?.["en"] === form.location.state : true)
                        )
                        .map((c) => (
                          <option key={c._id} value={language === "en" || language === "ta" ? c.masterValue?.["en"] : c.masterValue?.en}>
                            {/* {c.masterValue?.[i18n.language] || c.masterValue?.en} */}
                            {language === "en" || language === "ta" ? c.masterValue?.["en"] : c.masterValue?.en }
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Marital Status</label>
                    <select name="maritalStatus" value={form.maritalStatus} onChange={handleChange}>
                      <option value="">Select</option>
                      <option>Never Married</option>
                      <option>Divorced</option>
                      <option>Widowed</option>
                      <option>Separated</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Gender</label>
                    <select name="gender" value={form.gender} onChange={handleChange}>
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="form-group full">
                    <label>About Me</label>
                    <textarea name="about" value={form.about} onChange={handleChange} rows={3} placeholder="Write a short intro..." />
                  </div>
                </div>
              )}

              {/* ── PROFESSIONAL TAB ── */}
              {activeFormTab === "professional" && (
                <div className="form-grid">
                  <div className="form-group">
                    <label>Profession</label>
                    <select name="profession" value={form.profession} onChange={handleChange}>
                      <option value="">Select</option>
                      {profession.map((p) => (
                        <option key={p._id} value={p.masterValue?.["en"]}>
                          {p.masterValue?.["en"] || p.masterValue?.["en"]}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* if profession is others show input field to enter profession */}
                  {form.profession === "Others" && (
                    <div className="form-group">
                      <label>Enter Profession</label>
                      <input
                        type="text"
                        name="professionDetails"
                        value={form.professionDetails}
                        onChange={handleChange}
                        placeholder="Enter your profession"
                      />
                    </div>
                  )}
                  <div className="form-group">
                    <label>Education</label>
                    <select name="education" value={form.education} onChange={handleChange}>
                      <option value="">Select</option>
                      {education.map((e) => (
                        <option key={e._id} value={e.masterValue?.["en"]}>
                          {e.masterValue?.["en"] || e.masterValue?.["en"]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Monthly Income</label>
                    <select name="salary" value={form.salary} onChange={handleChange}>
                      <option value="">Select</option>
                      {incomeRanges.map((r, i) => (
                        <option key={i} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* ── LIFESTYLE TAB ── */}
              {activeFormTab === "lifestyle" && (
                <div className="form-grid">
                  <div className="form-group">
                    <label>Diet</label>
                    <select name="diet" value={form.diet} onChange={handleChange}>
                      <option value="">Select</option>
                      <option>Vegetarian</option>
                      <option>Non-Vegetarian</option>
                      <option>Eggetarian</option>
                      <option>Vegan</option>
                    </select>
                  </div>
                  {/* if gender is female not show smoking and drinking options */}
                  {form.gender !== "Female" && (
                    <>
                  <div className="form-group">
                    <label>Smoking</label>
                    <select name="smoking" value={form.smoking} onChange={handleChange}>
                      <option value="">Select</option>
                      <option>Never</option>
                      <option>Occasionally</option>
                      <option>Yes</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Drinking</label>
                    <select name="drinking" value={form.drinking} onChange={handleChange}>
                      <option value="">Select</option>
                      <option>Never</option>
                      <option>Occasionally</option>
                      <option>Yes</option>
                    </select>
                  </div>
                  </>
                  )}
                </div>
              )}

              {/* ── PREFERENCES TAB ── */}
              {activeFormTab === "preferences" && (
                <div className="form-grid">
                  <div className="form-group">
                    <label>Min Age</label>
                    <input name="prefAgeMin" type="number" value={form.prefAgeMin} onChange={handleChange} placeholder="21" />
                  </div>
                  <div className="form-group">
                    <label>Max Age</label>
                    <input name="prefAgeMax" type="number" value={form.prefAgeMax} onChange={handleChange} placeholder="30" />
                  </div>
                  <div className="form-group">
                    <label>Height</label>
                    <select name="prefHeight" value={form.prefHeight} onChange={handleChange}>
                      <option value="">Select</option>
                      {heights.map((h, i) => (
                        <option key={i} value={h}>{h}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Education</label>
                    {/* <input name="prefEducation" value={form.prefEducation} onChange={handleChange} placeholder="Any Degree" /> */}
                    <select name="prefEducation" value={form.prefEducation} onChange={handleChange}>
                      <option value="">Select</option>
                      {education.map((e) => (
                        <option key={e._id} value={e.masterValue?.["en"]}>
                          {e.masterValue?.["en"]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Religion</label>
                    <select
                      name="prefReligion"
                      value={form.prefReligion}
                      onChange={(e) => {
                        handleChange(e);
                        const selected = allFilterTypes.find(
                          (f) => f.masterType === "Religion" && f.masterValue?.["en"] === e.target.value
                        );
                        getCastBasedonReligion(selected?._id || null);
                      }}
                    >
                      <option value="">Select Religion</option>
                      {allFilterTypes
                        .filter((f) => f.isActive && f.masterType === "Religion")
                        .map((r) => (
                          <option key={r._id} value={r.masterValue?.["en"]}>
                            {r.masterValue?.["en"]}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Caste</label>
                    <select name="prefCaste" value={form.prefCaste} onChange={handleChange}>
                      <option value="">Select Caste</option>
                      {caste.map((c) => (
                        <option key={c._id} value={c.masterValue?.["en"]}>
                          {c.masterValue?.["en"]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Preferred State</label>
                    <select
                      value={form.prefState || ""}
                      onChange={(e) => {
                        const newState = e.target.value;
                        setForm((prev) => ({
                          ...prev,
                          prefState: newState,
                          //  only clear prefCity if user actually changed the state
                          prefCity: newState !== prev.prefState ? "" : prev.prefCity,
                        }));
                      }}
                    >
                      <option value="">Select State</option>
                      {allFilterTypes
                        .filter((f) => f.masterType === "State")
                        .map((s) => (
                          <option key={s._id} value={s.masterValue?.["en"]}>
                            {s.masterValue?.["en"]}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Preferred City</label>
                    <select
                      value={form.prefCity || ""}
                      onChange={(e) => setForm((prev) => ({ ...prev, prefCity: e.target.value }))}
                    >
                      <option value="">Select City</option>
                      {allFilterTypes
                        .filter(
                          (f) =>
                            f.masterType === "City" &&
                            (form.prefState ? f.parentId?.masterValue?.["en"] === form.prefState : true)
                        )
                        .map((c) => (
                          <option key={c._id} value={c.masterValue?.["en"]}>
                            {c.masterValue?.["en"]}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Profession</label>
                    <select name="prefProfession" value={form.prefProfession} onChange={handleChange}>
                      <option value="">Select</option>
                      {profession.map((p) => (
                        <option key={p._id} value={p.masterValue?.["en"]}>
                          {p.masterValue?.["en"]}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* if profession is others show input field to enter profession */}
                  {form.prefProfession === "Others" && (
                    <div className="form-group">
                      <label>Enter Profession</label>
                      <input
                        type="text"
                        name="prefProfessionDetails"
                        value={form.prefProfessionDetails}
                        onChange={handleChange}
                        placeholder="Enter your profession"
                      />
                    </div>
                  )}
                  <div className="form-group">
                    <label>Monthly Income</label>
                    <select name="prefSalaryMin" value={form.prefSalaryMin} onChange={handleChange}>
                      <option value="">Select</option>
                      {incomeRanges.map((r, i) => (
                        <option key={i} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="btn-save" onClick={handleSubmit} disabled={loading}>
                {loading ? "Saving..." : editId ? "Update Profile" : "Save Information"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Crop Modal ── */}
{showCropModal && cropSrc && (
  <div
    className="overlay"
    style={{ zIndex: 9999 }}
    onClick={(e) => e.target === e.currentTarget && setShowCropModal(false)}
  >
    <div
  className="modal"
  style={{
    maxWidth: 520,
    width: "95%",
    padding: 0,
    borderRadius: 16,
    overflow: "hidden",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
  }}
>
      {/* Header */}
      <div
        className="modal-header"
        style={{ padding: "16px 20px", borderBottom: "1px solid #e9d5ff" }}
      >
        <div className="modal-title" style={{ fontSize: 16 }}>
           Crop Your Photo
        </div>
        <button className="close-btn" onClick={() => { setShowCropModal(false); setCropSrc(null); }}>✕</button>
      </div>

      {/* Tip */}
      <div
        style={{
          padding: "8px 20px",
          background: "#faf5ff",
          fontSize: 12,
          color: "#7c3aed",
          borderBottom: "1px solid #e9d5ff",
        }}
      >
      </div>

      {/* Crop area */}
      <div style={{ padding: 24, background: "#1a1a2e", display: "flex", justifyContent: "center" }}>
        <ReactCrop
          crop={crop}
          onChange={(c) => {
      setCrop((prev) => ({ ...prev, x: c.x, y: c.y }));
    }}
    onComplete={(c) => {
      setCompletedCrop((prev) => ({ ...prev, x: c.x, y: c.y }));
    }}
    style={{ maxHeight: 340 }}
        >
          <img
            ref={imgRef}
            src={cropSrc}
            alt="crop-preview"
            style={{
              maxWidth: "100%",
              maxHeight: 340,
              display: "block",
            }}
            onLoad={(e) => {
        const { width, height } = e.currentTarget;
        const scaleX = width / e.currentTarget.naturalWidth;
  const scaleY = height / e.currentTarget.naturalHeight;
  const cropW = Math.min(500 * scaleX, width);
  const cropH = Math.min(494 * scaleY, height);
  const x = (width - cropW) / 2;
  const y = (height - cropH) / 2;
  const initialCrop = {
    unit: "px",
    x,
    y,
    width: cropW,
    height: cropH,
  };
  setCrop(initialCrop);
  setCompletedCrop(initialCrop);
      }}
            />
        </ReactCrop>
      </div>

      {/* Footer */}
      <div
        className="modal-footer"
        style={{ padding: "14px 20px", borderTop: "1px solid #e9d5ff", display: "flex", gap: 10, justifyContent: "flex-end" }}
      >
        <button
          className="btn-cancel"
          onClick={() => { setShowCropModal(false); setCropSrc(null); }}
        >
          Cancel
        </button>
        <button className="btn-save" onClick={handleCropConfirm}>
           Upload 
        </button>
      </div>
    </div>
  </div>
)}
      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
    </>
  );
}